// #4981 phase 2 (#5268) — which engine a device keeps its PouchDB data in.
//
// This module's ONLY import is `config-record-resolve.ts`, which itself imports
// nothing, so a plain-node test can still load this file — which is the thing the
// rule was always protecting. `local-storage-hygiene.ts` is the pattern for a module
// with no imports at all. The thin accessor that supplies the real browser and
// platform reads is `local-database-engine.ts` beside it.
//
// Nothing else may be imported here. The moment this file reaches a module that
// touches Angular, Capacitor or PouchDB, the node tests below it stop loading, which
// is exactly the failure the split described in the design doc exists to prevent.
//
// Why the split is forced, and why the state below lives on this side of it:
// design-docs/android-local-database-engine.md, "Two modules, and why the split
// is forced".

import { readUsableRolloutPercent } from './config-record-resolve';

/**
 * Where a device's PouchDB documents physically live.
 *
 * An enum rather than a boolean or a string union, per CLAUDE.md — the question
 * "which engine" has exactly two answers today and adding a third must be a
 * compile error at every reader, not a silently-false boolean.
 */
export enum LocalDatabaseEngine {
  /** The browser's IndexedDB. Every platform except the iOS native app today. */
  IndexedDb = 'IndexedDb',
  /** Our own SQLite file, reached through the CsSQLite Capacitor plugin. */
  Sqlite = 'Sqlite',
}

/**
 * The localStorage key recording which engine THIS device's data is actually in.
 *
 * It records a FACT about where the data is, never an intention about where it
 * should go — writing what the config WANTS here would be silent data loss. Why,
 * and why localStorage rather than a config value: the design doc, "The marker
 * records a FACT, not an intention".
 *
 * The literal is repeated in `DEVICE_LEVEL_LOCALSTORAGE_KEYS`
 * (local-storage-hygiene.ts) and in `clearSessionStorageAndCookies`
 * (app/reload-teardown.ts); tests cross-check both copies.
 */
export const LOCAL_DATABASE_ENGINE_KEY = 'cs_local_database_engine';

/** The stored value meaning {@link LocalDatabaseEngine.Sqlite}. */
export const LOCAL_DATABASE_ENGINE_VALUE_SQLITE = 'sqlite';

/** The stored value meaning {@link LocalDatabaseEngine.IndexedDb}. */
export const LOCAL_DATABASE_ENGINE_VALUE_IDB = 'idb';

/**
 * localStorage key set on the DECIDING boot, saying "copy this device's data on the
 * next page load". (#4981 phase 3)
 *
 * The migration deliberately spans two page loads. This marker is the only thing
 * carried between them, and it is an INTENTION — unlike
 * {@link LOCAL_DATABASE_ENGINE_KEY}, which records the FACT of where the data
 * actually is. Keeping the two apart is what makes a crash mid-copy harmless: the
 * intention survives, the fact does not move until the copy is verified.
 */
export const SQLITE_MIGRATION_REQUESTED_KEY = 'cs_sqlite_migration_requested';

/**
 * localStorage key counting boots that STARTED a copy. (#4981 phase 3)
 *
 * Spent when a copy begins, not when one fails — see R8. A boot killed mid-copy
 * writes nothing otherwise, and would restart the same doomed copy on every launch.
 *
 * **`COPY_` is load-bearing, not decoration.** `legacy-idb-cleanup.ts` exports a
 * `SQLITE_MIGRATION_ATTEMPTS_KEY` of its own, counting attempts at the #4771 cleanup
 * that DELETES leftover IndexedDB databases. That is the opposite operation on the
 * same storage, so the two names have to be tellable apart at a glance in a file that
 * imports both.
 */
export const SQLITE_MIGRATION_COPY_ATTEMPTS_KEY = 'cs_sqlite_migration_attempts';

/**
 * localStorage key counting consecutive aborts that happened BEFORE any copying.
 * (#4981 phase 3)
 *
 * Separate from the copy budget on purpose: R2 needs the enumeration to run before
 * the config read, so a device whose enumeration fails would otherwise spend its
 * whole copy budget before the kill switch was ever consulted. It resets at exactly
 * one point — the bounded config read returning an answer.
 */
export const SQLITE_MIGRATION_ABORTS_KEY = 'cs_sqlite_migration_aborts';

/**
 * localStorage key saying "never request a migration on this device again".
 * (#4981 phase 3)
 *
 * Set when either budget runs out. It exists because clearing the request marker is
 * NOT enough to stop anything: the deciding check runs later in the SAME page load,
 * sees an Android device still on IndexedDB with the controls saying yes, and writes
 * the request straight back.
 */
export const SQLITE_MIGRATION_BLOCKED_KEY = 'cs_sqlite_migration_blocked';

/**
 * localStorage key recording that phase 4 rolled this device BACK to IndexedDB.
 * (#4981 phase 3 honours it; phase 4 writes it.)
 *
 * Phase 3 never writes this. It only refuses to migrate while it is held, because a
 * rolled-back device sits in exactly the state the deciding check fires on — Android
 * native, on IndexedDB, controls still saying yes — and would otherwise copy its
 * whole database again on the next page load, every lap.
 *
 * **The hold is lifted by a re-arm GENERATION, never by deleting the key**, because
 * phase 4 also stores the superseded database names in it.
 *
 * **It is on BOTH localStorage wipes' survivor lists, and #5270 is what put it there.**
 * The note phase 3 left here said why it would have to be: the moment this key starts
 * carrying superseded database names, it has the same problem
 * `SQLITE_MIGRATION_TARGETS_KEY` below had — sign-out wipes it first and the code that
 * would have used the names runs second, so the names are gone and a full copy of the
 * user's database stays on disk with nothing left able to name it. That is what #5453
 * cost on a real phone, one key earlier. See
 * `design-docs/android-local-database-engine.md`, "Some notes must survive BOTH
 * localStorage wipes".
 */
export const SQLITE_MIGRATION_ROLLED_BACK_KEY = 'cs_sqlite_migration_rolled_back';

/**
 * localStorage key listing the SQLite databases a background copy has written into.
 * (#5449)
 *
 * A JSON array of LOGICAL database names (`userdb-fr_<uid>`, not `_pouch_userdb-fr_<uid>`).
 *
 * **It exists for sign-out, not for the copy.** Before #5449 a SQLite target never
 * outlived the boot that made it — the copy ran on the splash, and a failed attempt
 * deleted its target before the next try. A background copy keeps its target across
 * boots on purpose, so a device still running on IndexedDB can be carrying SQLite files
 * full of the signed-in user's data. Nothing else on the device can see them: the
 * database provider never held them, and phase 4's cleanup walks that same provider
 * list. Without this record, sign-out leaves one user's workouts and queued photos on
 * the device for the next person who signs in.
 *
 * It is written when a target is first opened, BEFORE anything is copied into it — the
 * same reasoning as spending the copy budget up front: a boot killed mid-copy must not
 * leave a file nothing knows about. So the list can legitimately name a database that
 * holds nothing yet, and deleting one that was never created is not an error.
 */
export const SQLITE_MIGRATION_TARGETS_KEY = 'cs_sqlite_migration_targets';

/**
 * localStorage key listing the local copies of client databases a coach opened in manage
 * mode on this device. (df-a00755b05133)
 *
 * A JSON array of LOGICAL local store names (`userdb-coach_<clientUid>`, with any
 * `__g<n>` reset suffix), the same shape as `SQLITE_MIGRATION_TARGETS_KEY` above.
 *
 * **It exists for sign-out, for the same reason that key does.** "Back to my account"
 * keeps the coach copy on purpose and reloads into the coach's own database, so by the
 * time the coach signs out the copy is no longer open and the database provider cannot
 * name it. On IndexedDB that does not matter, because sign-out deletes every IndexedDB
 * database on the origin. On SQLite the deleter is handed names, and the native plugin
 * has no "list the databases" call, so without this note each client's whole history
 * stays on the device. It is written whatever the engine, when the copy is opened, and
 * sign-out removes it. See design-docs/coach-mode.md, "The copy is kept when the coach
 * leaves manage mode".
 */
export const SQLITE_COACH_COPY_NAMES_KEY = 'cs_coach_copy_db_names';

/**
 * Which engine does this device keep its PouchDB data in? (#4981 phase 2)
 *
 * Pure, so the whole decision is testable with no browser. The accessor reads the
 * marker and passes it in; a read that threw arrives here as `null`.
 *
 * Falling back to IndexedDb is the safe direction only for a device that has NOT
 * migrated, which is why the accessor pairs it with a problem-tier report. The
 * three rules and what each one prevents: the design doc, "The marker records a
 * FACT, not an intention".
 */
export function decideLocalDatabaseEngine(
  isIosNative: boolean,
  isNative: boolean,
  markerValue: string | null | undefined,
): LocalDatabaseEngine {
  // if this is the iOS app, the engine is SQLite by platform and no marker applies
  if (isIosNative) return LocalDatabaseEngine.Sqlite;
  // otherwise SQLite needs a native platform (the adapter is only registered there)
  // and a marker that explicitly says the data is already in SQLite
  if (isNative && markerValue === LOCAL_DATABASE_ENGINE_VALUE_SQLITE) {
    return LocalDatabaseEngine.Sqlite;
  }
  return LocalDatabaseEngine.IndexedDb;
}

/**
 * Is the stored marker a value this code recognises? (#4981 phase 2)
 *
 * Absent is normal, NOT unrecognised — no device has a marker until phase 3
 * writes one. A present-but-unknown value is the reportable condition.
 */
export function isKnownLocalDatabaseEngineMarker(markerValue: string | null | undefined): boolean {
  if (markerValue === null || markerValue === undefined || markerValue === '') return true;
  return markerValue === LOCAL_DATABASE_ENGINE_VALUE_SQLITE
    || markerValue === LOCAL_DATABASE_ENGINE_VALUE_IDB;
}

// ---------------------------------------------------------------------------
// The resolver: the latch, the guarded read, and the problem-tier reports.
//
// This state lives on the import-free side so a plain-node test can drive both
// answers; under the accessor it could only ever reach IndexedDb, which makes the
// latch unprovable. See the design doc, "Two modules, and why the split is
// forced". The accessor holds the ONE production instance.
// ---------------------------------------------------------------------------

/** What this module needs in order to report a problem — a problem-tier report and nothing else. */
export type LocalDatabaseEngineReporter = (problemName: string, context: any) => void;

/** Reported when the marker holds a value this build does not understand. */
export const ENGINE_MARKER_UNRECOGNISED_PROBLEM = 'LocalDatabaseEngineMarkerUnrecognised';

/** Reported when reading the marker threw. */
export const ENGINE_MARKER_UNREADABLE_PROBLEM = 'LocalDatabaseEngineMarkerUnreadable';

/** Everything the resolver needs from the outside world. Each is a FUNCTION, not a
 *  value, so nothing is evaluated at module-import time — `Capacitor.isNativePlatform()`
 *  is a runtime call and must not run when this module is first loaded. */
export interface LocalDatabaseEngineDeps {
  isIosNative(): boolean;
  isNative(): boolean;
  /** Read the raw marker string. MAY THROW — the resolver catches it. See the
   *  accessor for why the whole storage lookup has to be inside this one closure. */
  readMarker(): string | null;
}

/** The latched engine answer plus its reporting, as one object. */
export interface LocalDatabaseEngineResolver {
  get(): LocalDatabaseEngine;
  /**
   * Has anything asked the engine question yet on this page load? (#4981 phase 3)
   *
   * Phase 3's migration must run BEFORE the first `get()`. The answer is latched on
   * that first call, so a migration that wrote the engine marker afterwards would
   * leave the app spending the rest of the session on the OLD answer while the
   * user's data sat in the NEW engine — every read and write going to a database
   * that is no longer the real one.
   *
   * This is what lets the migration ENFORCE that ordering instead of a comment
   * hoping for it: if the latch has already been taken, it copies nothing and
   * reports. That abort is a wiring bug rather than a device problem, so it
   * deliberately spends none of the migration's retry budget.
   */
  hasBeenRead(): boolean;
  setReporter(fn: LocalDatabaseEngineReporter): void;
  resetForTests(): void;
}

/**
 * Build a resolver that answers the engine question once and then never changes
 * its mind (#4981 phase 2).
 *
 * **The latch is a data-loss guard, not a performance nicety** —
 * `recreateLocalDbAsync` reopens the database mid-session and asks again, and
 * phase 3 writes the marker while the app runs. Reports raised before a reporter
 * is wired are buffered, one per condition, because the first read can happen
 * before `AppComponent` wires one.
 *
 * The latch is taken on the first `get()`, by whichever caller runs first —
 * expected to be `getLocalAdapterOpts` during database open, but phase 3 must
 * verify that rather than assume it. Full reasoning: the design doc, "The answer
 * is latched, and why that is a data-loss guard".
 */
export function createLocalDatabaseEngineResolver(
  deps: LocalDatabaseEngineDeps,
): LocalDatabaseEngineResolver {
  let latched: LocalDatabaseEngine | undefined;
  let reporter: LocalDatabaseEngineReporter | null = null;
  // at most one buffered report per condition, first occurrence kept
  const buffered = new Map<string, any>();

  function report(problemName: string, context: any): void {
    if (reporter) {
      reporter(problemName, context);
      return;
    }
    if (!buffered.has(problemName)) buffered.set(problemName, context);
  }

  return {
    get(): LocalDatabaseEngine {
      if (latched !== undefined) return latched;

      let markerValue: string | null = null;
      let readThrew = false;
      try {
        markerValue = deps.readMarker();
      } catch (error) {
        // the read itself was refused. Falling back to IndexedDb is safe only for a
        // device that has NOT migrated — hence a report rather than silent absorption.
        readThrew = true;
        markerValue = null;
        report(ENGINE_MARKER_UNREADABLE_PROBLEM, {
          message: (error as Error)?.message,
          name: (error as Error)?.name,
        });
      }

      // OUTSIDE the try on purpose: a reporter that throws must not be caught here
      // and misfiled as an unreadable read.
      if (!readThrew && !isKnownLocalDatabaseEngineMarker(markerValue)) {
        // a value this build does not understand means the device silently falls
        // back to IndexedDB — invisible without a report, and wrong once it has migrated
        report(ENGINE_MARKER_UNRECOGNISED_PROBLEM, { marker_value: markerValue });
      }

      latched = decideLocalDatabaseEngine(deps.isIosNative(), deps.isNative(), markerValue);
      return latched;
    },

    hasBeenRead(): boolean {
      return latched !== undefined;
    },

    setReporter(fn: LocalDatabaseEngineReporter): void {
      // AppComponent is the only caller and calls this once; a second call would
      // simply replace the reporter, which is harmless but not a designed-for case.
      reporter = fn;
      for (const [problemName, context] of buffered) fn(problemName, context);
      buffered.clear();
    },

    resetForTests(): void {
      latched = undefined;
      reporter = null;
      buffered.clear();
    },
  };
}

// ---------------------------------------------------------------------------
// R6 — the rollout controls. Pure decisions, mirroring `shouldAutoResolveConflicts`
// in sync-conflict-resolve-decisions.ts: an injected bucket function, a prefixed
// hash input, and fail-to-disabled on every unusable value.
//
// NOTHING CALLS THESE IN PHASE 2. They ship with tests and no callers; phase 3 is
// what reads the config and uses them.
// ---------------------------------------------------------------------------

/**
 * The string handed to the bucket function, with the device id appended.
 *
 * **Changing it is not a refactor** — a different prefix moves every device across
 * the band boundary at once, which a monotonic ramp must never do. The prefix
 * exists so this cohort is uncorrelated with every other ramp sharing the hash.
 */
export const ANDROID_SQLITE_BUCKET_PREFIX = 'android-sqlite:';

/**
 * Has an operator switched this user on for one of the per-user SQLite keys?
 * (#4981, reworked 2026-09-30)
 *
 * `value` is what the config service resolved for THIS user, so it is their own
 * per-user entry when one exists. On means exactly `true`. Anything else, including
 * the old list-of-user-ids shape, is off, so every malformed value disables the
 * feature. The global value of these keys is always `false`; the reason is at
 * `USER_LIST_CONFIG_KEYS` in config-keys.ts.
 *
 * Exported separately from the combined decisions because the migration and the
 * rollback keys apply the same rule.
 */
export function isUserSwitchedOn(value: unknown, uid: string | undefined): boolean {
  // no user means no per-user entry could have been resolved for them
  if (!uid) return false;
  return value === true;
}

/**
 * Should THIS DEVICE migrate its local data onto the SQLite engine?
 * (#4981 phase 2, used by phase 3)
 *
 * **The per-user switch is checked FIRST**, and the two controls can legitimately
 * disagree — a named colleague while the percentage is still 0 is phase 4's first
 * ramp stage. The switch is keyed by USER, the bucket by DEVICE, on purpose. The
 * test is strictly `<`, so raising the percent only ever ADDS devices, and every
 * unusable percent reads as 0.
 *
 * Why the two are keyed differently, and what a regenerated `deviceId` after
 * sign-out does to the band: the design doc, "The rollout controls, added and left
 * switched off".
 */
export function shouldMigrateThisDeviceToSqlite(
  userSwitchValue: unknown,
  percentValue: unknown,
  uid: string | undefined,
  deviceId: string | undefined,
  bucketFor: (identifier: string) => number,
): boolean {
  // a user switched on wins outright, whatever the percentage says
  if (isUserSwitchedOn(userSwitchValue, uid)) return true;

  // if the configured percent is unusable in any way, treat the migration as off —
  // an unreadable dial must not start copying anybody's database. What counts as
  // unusable, and why a shape like `true` has to be refused before the conversion
  // rather than after it, is `readUsableRolloutPercent`.
  const asNumber = readUsableRolloutPercent(percentValue);
  if (asNumber === undefined) return false;

  // no device id means no stable bucket to place this device in
  if (!deviceId) return false;
  return bucketFor(`${ANDROID_SQLITE_BUCKET_PREFIX}${deviceId}`) < asNumber;
}

// ---------------------------------------------------------------------------
// #4981 phase 4 (#5270) — the rollback controls and the notes the way back leaves
// behind. Same shapes as the migration controls above, and the same
// fail-to-disabled rule, except where a comment says the direction is deliberately
// the other way.
// ---------------------------------------------------------------------------

/**
 * localStorage key recording that this device has been ASKED to roll back, so the
 * next page load does it. (#5270)
 *
 * Mirrors {@link SQLITE_MIGRATION_REQUESTED_KEY}, and for the same reason: the
 * decision needs the config, which needs a database read, and the boot path must not
 * wait on one. So a session decides and a later cold boot acts — re-checking the
 * controls before it does anything, so taking a person back OFF the rollback list
 * still takes effect immediately.
 *
 * Deliberately NOT on either `localStorage` survivor path. It is a request about the
 * signed-in account's data, and a sign-out wipes it exactly as it wipes the forward
 * direction's request.
 */
export const SQLITE_ROLLBACK_REQUESTED_KEY = 'cs_sqlite_rollback_requested';

/**
 * localStorage key counting how many page loads have STARTED a reverse copy. (#5270)
 *
 * Spent before the first read, like the forward direction's, so a page load killed
 * mid-copy still spends an attempt and a device cannot restart the same doomed copy
 * for ever.
 */
export const SQLITE_ROLLBACK_ATTEMPTS_KEY = 'cs_sqlite_rollback_attempts';

/**
 * localStorage key set when this device has spent its rollback budget. (#5270)
 *
 * A device that cannot copy itself back stays on SQLite with all of its data — which
 * is where it already was. The marker stops it trying on every launch, and an
 * operator who needs it moved anyway has the app-release route the plan names.
 */
export const SQLITE_ROLLBACK_BLOCKED_KEY = 'cs_sqlite_rollback_blocked';

/**
 * localStorage key holding the rollback-completion record — the COMMIT POINT of a
 * rollback. (#5270)
 *
 * One value, written in a single `setItem` after the switch half verifies. While it
 * exists the rollback's tail is unfinished and the next boot finishes it; the tail's
 * last step clears it. Its shape, and why it cannot be a sequence of separate writes,
 * are on `RollbackCompletionRecord` in `sqlite-migration-decisions.ts`.
 *
 * **It goes on BOTH `localStorage` survivor paths**, like the engine marker, and for a
 * sharper version of the same reason: losing it mid-tail leaves the device reading one
 * engine while a full copy of its data sits at a generation nothing names, with the
 * sign-out deleter already disarmed. That is precisely the orphan the record exists to
 * prevent.
 */
export const SQLITE_ROLLBACK_COMPLETION_KEY = 'cs_sqlite_rollback_completion';

/**
 * localStorage key counting successful boots on the SQLite engine. (#5270)
 *
 * The grace period before a device deletes an IndexedDB copy it no longer uses.
 * "Successful" means the boot reached `BootStage.Ready`; anything short of that does not
 * count, and the counter is incremented at that point, once per page load.
 *
 * **It counts on WHICHEVER ENGINE the device is using, and the name is narrower than the
 * meaning (#5270 step 6b).** On SQLite it is the grace period before the pre-migration
 * IndexedDB copy is deleted. On a device that has ROLLED BACK — which is on IndexedDB
 * again, carrying superseded names in the rolled-back marker — it is the grace period
 * before the generation that device abandoned is deleted. One key is safe for both
 * because it is reset on every engine change, so the two meanings can never mix in one
 * stored number; `shouldCountReadyBoot` in `sqlite-migration-decisions.ts` states the
 * rule. The key was NOT renamed: a rename is a data migration plus an edit to each
 * survivor list, for no change in behaviour.
 *
 * **Deliberately NOT "consecutive", and the distinction is worth the sentence.** A boot that
 * wedges before `Ready` simply does not count — nothing resets the counter because of it —
 * so `Ready`, wedge, `Ready` counts as two. That is the intended meaning: a device that
 * wedges occasionally and recovers has still shown its SQLite data is readable, which is
 * what is being counted. The abort budget in `sqlite-migration-decisions.ts` IS consecutive
 * and does reset, so the two words are load-bearing in this area.
 *
 * **It goes on BOTH `localStorage` survivor paths**, for the reason phase 2's R7 gives for
 * the engine marker: a PARTIAL teardown — the bounded one that may die before it finishes,
 * or the fatal-error reset — must not silently zero the counter and hand the device a fresh
 * grace period it has already served. A COMPLETED sign-out zeroes it deliberately, as the
 * last thing it does. Those two are not a contradiction, and both halves have to be said or
 * the next reader will delete one of them.
 */
export const SQLITE_READY_BOOTS_KEY = 'cs_sqlite_ready_boots';

/**
 * localStorage key that pre-loads the boot counter, for testing only. (#5270)
 *
 * The human verification of the cleanup would otherwise need ten real app launches on a
 * phone. An operator sets this to a number and the next boot starts counting from it.
 *
 * **Deliberately a separate key rather than letting somebody edit the counter directly.**
 * The counter is a fact the app maintains; this is an instruction a person left. Keeping
 * them apart means the app can reset the counter on a rollback, a re-migration or a sign-out
 * without ever wondering whether it is about to destroy somebody's test set-up — and it
 * means a value left behind on a real device is visible for what it is.
 *
 * Deliberately NOT on either survivor path: a sign-out should take a tester's set-up with
 * it, like every other user-scoped value.
 */
export const SQLITE_READY_BOOTS_TEST_OVERRIDE_KEY = 'cs_sqlite_ready_boots_test_override';

/** Config document ids the rolling-back boot re-reads, for its kill switch. (#5270) */
export const ROLLBACK_ALLOW_LIST_DOC_ID = 'config/AndroidSqliteRollbackAllowList';
export const ROLLBACK_PERCENT_DOC_ID = 'config/AndroidSqliteRollbackToIndexedDbPercent';

/**
 * The string handed to the bucket function for the ROLLBACK ramp. (#5270)
 *
 * A different prefix from {@link ANDROID_SQLITE_BUCKET_PREFIX}, deliberately. Sharing
 * one would make the two ramps perfectly correlated: the first devices to migrate
 * would be the first devices to roll back, so a 5% rollback would take back exactly
 * the 5% that moved first rather than a fresh sample of the migrated population.
 */
export const ANDROID_SQLITE_ROLLBACK_BUCKET_PREFIX = 'android-sqlite-rollback:';

/**
 * Should THIS DEVICE copy its local data back onto the IndexedDB engine? (#5270)
 *
 * The mirror image of {@link shouldMigrateThisDeviceToSqlite}, with the same shapes,
 * the same strictly-`<` test and the same fail-to-disabled rule on every unusable
 * value: a rollback dial nobody can read must roll nobody back.
 *
 * **The per-user switch is checked FIRST, and during an incident it is the control an
 * operator actually reaches for.** A support ticket names a person; that person is
 * moved back without touching anybody else, and without waiting for a percentage that
 * would take a share of everybody with them.
 *
 * Keyed the same way round as the forward controls — the switch by USER, the bucket by
 * DEVICE — because what is being moved is per-device data, so one person's phone and
 * tablet can legitimately sit on different engines.
 */
export function shouldRollBackThisDeviceToIndexedDb(
  userSwitchValue: unknown,
  percentValue: unknown,
  uid: string | undefined,
  deviceId: string | undefined,
  bucketFor: (identifier: string) => number,
): boolean {
  // a user switched on wins outright, whatever the percentage says
  if (isUserSwitchedOn(userSwitchValue, uid)) return true;

  // If the configured percent is unusable in any way, treat the rollback as off — a
  // rollback dial nobody can read must roll nobody back. What counts as unusable, and
  // why a shape like `true` has to be refused before the conversion rather than after
  // it, is `readUsableRolloutPercent`.
  const asNumber = readUsableRolloutPercent(percentValue);
  if (asNumber === undefined) return false;

  // no device id means no stable bucket to place this device in
  if (!deviceId) return false;
  return bucketFor(`${ANDROID_SQLITE_ROLLBACK_BUCKET_PREFIX}${deviceId}`) < asNumber;
}
